import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, ArrowRight, Mail } from 'lucide-react';
import { business, services } from '../data/siteData';
import { PageHeader } from './About';

export default function Contact() {
  return (
    <div>
      <PageHeader
        title="Contact Us"
        subtitle="Get in touch by phone for a fast, free estimate — we serve AB & SK."
        crumb="Contact"
      />

      <section className="max-w-6xl mx-auto px-6 py-20">
        {/* Big Phone CTA */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500 to-amber-400 p-10 md:p-14 shadow-xl mb-12">
          <div className="relative grid md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-2">
              <div className="text-xs font-bold tracking-[0.25em] text-slate-900/80 uppercase mb-3">
                Call Today — Free Estimates
              </div>
              <h2 className="text-3xl md:text-5xl font-black text-slate-900 leading-tight">
                Speak with our team now
              </h2>
              <p className="mt-3 text-slate-800 max-w-xl">
                The fastest way to get a quote or schedule a site visit is to give us a call.
                We answer during business hours and return all messages within one business day.
              </p>
            </div>
            <div className="flex md:justify-end">
              <a
                href={`tel:${business.phoneRaw}`}
                className="inline-flex items-center gap-3 bg-slate-900 hover:bg-slate-800 text-white font-bold px-7 py-4 rounded-md shadow-xl text-lg transition-colors"
              >
                <Phone size={20} /> {business.phone}
              </a>
            </div>
          </div>
        </div>

        {/* Info cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <a
            href={`tel:${business.phoneRaw}`}
            className="group bg-white border border-slate-200 rounded-xl p-7 hover:border-amber-500 hover:shadow-xl transition-all"
          >
            <div className="w-12 h-12 rounded-md bg-amber-500 text-slate-900 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Phone size={22} />
            </div>
            <div className="mt-5 text-xs font-bold tracking-widest uppercase text-amber-600">
              Phone
            </div>
            <div className="mt-1 text-lg font-extrabold text-slate-900">{business.phone}</div>
            <div className="text-sm text-slate-500 mt-2">Tap to call now</div>
          </a>

          <a
            href={`mailto:${business.email}`}
            className="group bg-white border border-slate-200 rounded-xl p-7 hover:border-amber-500 hover:shadow-xl transition-all"
          >
            <div className="w-12 h-12 rounded-md bg-amber-500 text-slate-900 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Mail size={22} />
            </div>
            <div className="mt-5 text-xs font-bold tracking-widest uppercase text-amber-600">
              Email
            </div>
            <div className="mt-1 text-sm md:text-base font-extrabold text-slate-900 break-all leading-snug">
              {business.email}
            </div>
            <div className="text-sm text-slate-500 mt-2">Tap to email us</div>
          </a>

          <div className="bg-white border border-slate-200 rounded-xl p-7">
            <div className="w-12 h-12 rounded-md bg-amber-500 text-slate-900 flex items-center justify-center">
              <MapPin size={22} />
            </div>
            <div className="mt-5 text-xs font-bold tracking-widest uppercase text-amber-600">
              Address
            </div>
            <div className="mt-1 text-base font-extrabold text-slate-900 leading-snug">
              3545 32 Avenue NE,
              <br />Calgary, Alberta T1Y 6M6
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-7">
            <div className="w-12 h-12 rounded-md bg-amber-500 text-slate-900 flex items-center justify-center">
              <Clock size={22} />
            </div>
            <div className="mt-5 text-xs font-bold tracking-widest uppercase text-amber-600">
              Hours
            </div>
            <div className="mt-1 font-bold text-slate-900 text-sm">{business.hours}</div>
            <div className="font-bold text-slate-900 text-sm">{business.hoursWeekend}</div>
            <div className="text-sm text-slate-500 mt-1">Sun: Closed</div>
          </div>
        </div>

        {/* Services quick links */}
        <div className="mt-14 bg-slate-50 border border-slate-200 rounded-xl p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="text-xs font-bold tracking-[0.25em] text-amber-600 uppercase">
                Looking for a specific service?
              </div>
              <h3 className="mt-2 text-2xl md:text-3xl font-extrabold text-slate-900">
                Mention this when you call
              </h3>
              <p className="mt-2 text-slate-600">
                Let us know which service you’re interested in and we’ll prepare a quote on the
                spot.
              </p>
            </div>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold px-6 py-3 rounded-md transition-colors shrink-0"
            >
              View All Services <ArrowRight size={16} />
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {services.map((s) => (
              <span
                key={s.id}
                className="px-3 py-1.5 rounded-full bg-white text-slate-700 text-sm font-medium border border-slate-200"
              >
                {s.title}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
